import React, { useEffect, useRef } from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { Plus, X } from './icons';
import { colors, formes, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';
import { TEXTE_LTR } from '../i18n/sens';

interface BulletsEditorProps {
  value: string[];
  onChange: (bullets: string[]) => void;
  testID?: string;
}

// Édition des points d'une entrée : une ligne grise par point, retrait par la croix, ajout par le lien.
export function BulletsEditor({ value, onChange, testID }: BulletsEditorProps) {
  // Rang du point qui vient d'être ajouté : il prend le focus à l'apparition, sans quoi un point
  // créé sous le bas de l'écran passerait inaperçu.
  const nouveau = useRef(-1);
  // Une seule fois : sans cela, tout champ remonté plus tard à ce rang reprendrait le focus.
  useEffect(() => {
    nouveau.current = -1;
  });
  const update = (index: number, text: string) =>
    onChange(value.map((b, i) => (i === index ? text : b)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => {
    nouveau.current = value.length;
    onChange([...value, '']);
  };

  return (
    <View testID={testID} style={styles.container}>
      {value.map((bullet, index) => (
        <View key={index} style={styles.row}>
          <TextInput
            testID={`${testID}-bullet-${index}`}
            style={styles.input}
            accessibilityLabel={`${t('editors.bullets.placeholder')} ${index + 1}`}
            placeholder={t('editors.bullets.placeholder')}
            placeholderTextColor={colors.grey500}
            multiline
            autoFocus={index === nouveau.current}
            value={bullet}
            onChangeText={(text) => update(index, text)}
          />
          <Pressable
            testID={`${testID}-remove-${index}`}
            accessibilityRole="button"
            accessibilityLabel={`${t('a11y.removeBullet')} ${index + 1}`}
            onPress={() => remove(index)}
            style={styles.remove}
          >
            <X size={16} color={colors.grey500} strokeWidth={2.4} />
          </Pressable>
        </View>
      ))}
      <Pressable testID={`${testID}-add`} accessibilityRole="button" onPress={add} style={styles.add}>
        <Plus size={15} color={colors.electricDeep} strokeWidth={3} />
        <Text style={styles.addText}>{t('editors.bullets.add')}</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: formes.rayon.md,
    backgroundColor: colors.grey100,
    paddingStart: spacing.md,
  },
  // Les points sont le texte du CV : saisis de gauche à droite même dans une interface en arabe.
  input: {
    ...TEXTE_LTR,
    flex: 1,
    minWidth: 0,
    minHeight: formes.cible,
    paddingVertical: spacing.sm,
    fontSize: typography.body.size,
    color: colors.night,
  },
  remove: { width: formes.cible, height: formes.cible, alignItems: 'center', justifyContent: 'center' },
  add: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    minHeight: formes.cible,
  },
  addText: { fontSize: 14, fontWeight: '800', color: colors.electricDeep },
});
